import useQuery from 'hooks/useQuery'
import getAccessToken from 'hooks/getAccessToken'

const { REACT_APP_API_URL } = process.env
const BASE = '/custom-forms'

const shortId = (id) => (id ? String(id).split('/').pop() : id)

// Formularios del panel del paciente: disponibles para cargar + los que ya tienen
// cargas de este paciente (aunque estén ocultos/restringidos), en solo-lectura.
export const useCustomForms = (consumerID) =>
  useQuery(window._l(`${BASE}?consumerId=${encodeURIComponent(consumerID || '')}`).substr(1), {
    enabled: !!consumerID,
    staleTime: 0,
    refetchOnMount: 'always',
  })

// Mis formularios (gestión) — incluye deshabilitados.
export const useMyForms = (enabled) =>
  useQuery(window._l(`${BASE}?manage=1`).substr(1), { enabled: !!enabled })

// Cargas (controles) de un formulario para un paciente. Trae también las eliminadas
// (soft-delete) para poder ofrecerlas bajo "Ver eliminados"; se separan en el front.
export const useFormRecords = (formID, consumerID) =>
  useQuery(
    window._l(`${BASE}/${shortId(formID)}/records?consumerId=${encodeURIComponent(consumerID || '')}&deleted=true`).substr(1),
    { enabled: !!(formID && consumerID) }
  )

// Todas las cargas del paciente (todos los formularios) — solo para el badge de conteo
// del toggle "Formularios" en la barra de filtros de la HCE (independiente del panel).
export const useConsumerFormRecordsCount = (consumerID) =>
  useQuery(
    window._l(`${BASE}/consumer/${shortId(consumerID)}/records`).substr(1),
    { enabled: !!consumerID }
  )

// Helper imperativo para escrituras (POST/PUT). Devuelve una fn ligada al token.
export const useApi = () => {
  const accessToken = getAccessToken()
  return async (path, { method = 'POST', body } = {}) => {
    const url = `${REACT_APP_API_URL}/${window._l(path).substr(1)}`
    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken.value}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    })
    const data = await res.json().catch(() => null)
    if (!res.ok) throw new Error(data?.error || 'error')
    return data
  }
}

// Builders de path (para el helper imperativo).
export const paths = {
  create: () => BASE,
  update: (formID) => `${BASE}/${shortId(formID)}`, // PATCH: metadata parcial
  updateColumn: (formID, columnID) => `${BASE}/${shortId(formID)}/columns/${columnID}`, // PATCH: una columna
  disable: (formID) => `${BASE}/${shortId(formID)}/disable`,
  enable: (formID) => `${BASE}/${shortId(formID)}/enable`,
  createRecord: (formID) => `${BASE}/${shortId(formID)}/records`,
  updateRecord: (formID, recordID) => `${BASE}/${shortId(formID)}/records/${shortId(recordID)}`, // PATCH: celda/merge/fecha
  deleteRecord: (formID, recordID) => `${BASE}/${shortId(formID)}/records/${shortId(recordID)}/delete`,
}
